import React, { useMemo, useState } from 'react';
import { Search, BarChart3, List, MoreHorizontal } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  fmtXof, initials, avatarColor, rowStatus, MEDALS, progressColor, ROLE_LABEL,
} from './perfMeta';

const FILTERS = [
  { key: 'all', label: 'Tous' },
  { key: 'with_target', label: 'Avec objectif' },
  { key: 'no_target', label: 'Sans objectif' },
  { key: 'with_ca', label: 'Avec CA' },
  { key: 'no_activity', label: 'Sans activité' },
];

const matchFilter = (r, f) => {
  if (f === 'all') return true;
  if (f === 'with_target') return (r.ca?.target || 0) > 0;
  if (f === 'no_target') return (r.ca?.target || 0) === 0;
  if (f === 'with_ca') return (r.ca?.realised || 0) > 0;
  if (f === 'no_activity') return (r.ca?.realised || 0) === 0 && (r.ops?.realised || 0) === 0 && (r.invoices?.realised || 0) === 0;
  return true;
};

// Section « Classement commercial »
export const LeaderboardTable = ({ rows = [], totals, onOpen }) => {
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all');
  const [chart, setChart] = useState(false);

  const filtered = useMemo(() => rows
    .filter((r) => matchFilter(r, filter))
    .filter((r) => (r.commercialName || '').toLowerCase().includes(q.toLowerCase())), [rows, filter, q]);

  const maxCa = Math.max(...rows.map((r) => r.ca?.realised || 0), 1);

  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm" data-testid="perf-leaderboard">
      <div className="border-b border-gray-100 px-5 py-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold tracking-tight text-gray-900">Classement commercial</h2>
            <p className="text-xs text-gray-500">Comparez les performances, objectifs, opérations payées et commissions.</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un commercial…" className="h-8 w-48 pl-8 text-xs" data-testid="perf-search" />
            </div>
            <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setChart((c) => !c)} data-testid="perf-chart-toggle">
              {chart ? <List className="mr-1 h-3.5 w-3.5" /> : <BarChart3 className="mr-1 h-3.5 w-3.5" />}
              {chart ? 'Voir en tableau' : 'Voir en graphique'}
            </Button>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {FILTERS.map((f) => (
            <button key={f.key} type="button" onClick={() => setFilter(f.key)} data-testid={`perf-filter-${f.key}`}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${filter === f.key ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {chart ? (
        <div className="space-y-2 p-5" data-testid="perf-chart">
          {filtered.map((r) => (
            <div key={r.commercialId} className="flex items-center gap-3">
              <span className="w-32 shrink-0 truncate text-sm text-gray-600">{r.commercialName}</span>
              <div className="relative h-5 flex-1 overflow-hidden rounded bg-gray-100">
                <div className="absolute inset-y-0 left-0 rounded bg-emerald-500" style={{ width: `${Math.max(2, (r.ca.realised / maxCa) * 100)}%` }} />
              </div>
              <span className="w-32 shrink-0 text-right text-sm font-semibold tabular-nums text-gray-800">{fmtXof(r.ca.realised)}</span>
            </div>
          ))}
          {filtered.length === 0 && <p className="py-8 text-center text-sm text-gray-400">Aucun commercial.</p>}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-wide text-gray-400">
                <th className="px-3 py-2 text-center font-medium">#</th>
                <th className="px-2 py-2 text-left font-medium">Commercial</th>
                <th className="px-2 py-2 text-center font-medium">Clients</th>
                <th className="px-2 py-2 text-right font-medium">CA réalisé</th>
                <th className="px-2 py-2 text-right font-medium">Objectif</th>
                <th className="px-2 py-2 text-left font-medium">Progression</th>
                <th className="px-2 py-2 text-center font-medium">Ops payées</th>
                <th className="px-2 py-2 text-center font-medium">Fact.</th>
                <th className="px-2 py-2 text-right font-medium">Commission</th>
                <th className="px-2 py-2 text-center font-medium">Statut</th>
                <th className="px-3 py-2 text-right font-medium" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.map((r, idx) => {
                const st = rowStatus(r);
                const rank = rows.indexOf(r) + 1;
                return (
                  <tr key={r.commercialId} onClick={() => onOpen(r)} data-testid={`perf-row-${r.commercialId}`} className="cursor-pointer transition-colors hover:bg-gray-50/70">
                    <td className="px-3 py-2.5 text-center text-sm">{MEDALS[rank] || <span className="text-gray-400">{rank}</span>}</td>
                    <td className="px-2 py-2.5">
                      <div className="flex items-center gap-2.5">
                        <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white ${avatarColor(r.commercialId)}`}>{initials(r.commercialName)}</span>
                        <div className="min-w-0">
                          <div className="truncate font-medium text-gray-800">{r.commercialName}</div>
                          <div className="text-[11px] text-gray-400">{ROLE_LABEL[r.role] || 'Commercial'}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-2 py-2.5 text-center tabular-nums text-gray-500">{r.portfolioSize ?? 0}</td>
                    <td className="px-2 py-2.5 text-right font-semibold tabular-nums text-gray-900">{fmtXof(r.ca.realised)}</td>
                    <td className="px-2 py-2.5 text-right tabular-nums">
                      {r.ca.target > 0 ? <span className="text-gray-600">{fmtXof(r.ca.target)}</span> : <span className="rounded-md bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 ring-1 ring-amber-200">Non défini</span>}
                    </td>
                    <td className="px-2 py-2.5">
                      <div className="flex items-center gap-1.5">
                        <div className="h-1.5 w-16 overflow-hidden rounded-full bg-gray-100">
                          <div className={`h-full rounded-full ${progressColor(r.ca.pct)}`} style={{ width: `${Math.min(100, r.ca.pct || 0)}%` }} />
                        </div>
                        <span className="text-[11px] tabular-nums text-gray-500">{Math.round(r.ca.pct || 0)}%</span>
                      </div>
                    </td>
                    <td className="px-2 py-2.5 text-center tabular-nums text-gray-600">{r.ops.realised}</td>
                    <td className="px-2 py-2.5 text-center tabular-nums text-gray-500">{r.invoices?.realised ?? 0}</td>
                    <td className="px-2 py-2.5 text-right tabular-nums">
                      <div className="text-gray-800">{fmtXof((r.commissions?.pending || 0) + (r.commissions?.approved || 0))}</div>
                      {(r.commissions?.paid || 0) > 0 && <div className="text-[10px] text-emerald-600">{fmtXof(r.commissions.paid)} versé</div>}
                    </td>
                    <td className="px-2 py-2.5 text-center"><span className={`inline-flex rounded-md px-2 py-0.5 text-xs font-medium ring-1 ${st.cls}`}>{st.label}</span></td>
                    <td className="px-3 py-2.5 text-right" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button variant="ghost" size="sm" className="h-7 w-7 p-0"><MoreHorizontal className="h-4 w-4 text-gray-400" /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => onOpen(r)}>Voir le détail</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={11} className="py-12 text-center text-gray-400" data-testid="perf-empty">Aucun commercial pour ce filtre.</td></tr>
              )}
            </tbody>
            {totals && (
              <tfoot>
                <tr className="border-t-2 border-gray-100 bg-gray-50 font-bold">
                  <td className="px-3 py-3" />
                  <td className="px-2 py-3 text-gray-700">Total équipe</td>
                  <td className="px-2 py-3 text-center tabular-nums text-gray-600">{rows.reduce((a, r) => a + (r.portfolioSize || 0), 0)}</td>
                  <td className="px-2 py-3 text-right tabular-nums text-gray-900">{fmtXof(totals.ca)}</td>
                  <td className="px-2 py-3 text-right tabular-nums text-gray-600">{fmtXof(totals.caTarget)}</td>
                  <td className="px-2 py-3 text-left text-xs tabular-nums text-gray-500">{Math.round(totals.progression || 0)}%</td>
                  <td className="px-2 py-3 text-center tabular-nums text-gray-700">{totals.ops}</td>
                  <td className="px-2 py-3" />
                  <td className="px-2 py-3 text-right tabular-nums text-gray-700">{fmtXof((totals.commissions?.remaining || 0))}</td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </div>
  );
};

export default LeaderboardTable;
